import React from "react";
import styled from "styled-components";
import { Modal as AntModal, Button as AntButton } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, Check, X } from "lucide-react"; // Using lucide icons for consistency

// Styled components for the enhanced modal
const StyledModal = styled(AntModal)`
  .ant-modal-content {
    border-radius: 1rem;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border: 1px solid #e2e8f0;
  }

  .ant-modal-header {
    background: white;
    border-bottom: 1px solid #f1f5f9;
    padding: 1.25rem 1.5rem;
  }

  .ant-modal-title {
    color: #1e40af;
    font-weight: 600;
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .ant-modal-body {
    padding: 1.5rem;
    font-size: 0.95rem;
    color: #475569;
    line-height: 1.6;
  }

  .ant-modal-footer {
    border-top: 1px solid #f1f5f9;
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: flex-end;
  }
  
  .ant-modal-close {
    color: #64748b;
    
    &:hover {
      color: #1e40af;
    }
  }
`;

const StyledButton = styled(AntButton)`
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 0.5rem;
  height: auto;
  padding: 0.6rem 1.5rem;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover, &:focus {
    background: #1e3a8a;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(motion.div)`
  text-align: center;
`;

const NoResultModal = ({
  footer,
  btnName,
  setModalOpen,
  cancel,
  modalOpen,
  children,
  click,
  msg,
  icon = "alert", // Default icon is alert, can be 'alert', 'success', or 'none'
}) => {
  // Animation variants for content
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.3,
        ease: "easeOut" 
      }
    }
  };

  // Render appropriate icon based on prop
  const renderIcon = () => {
    switch(icon) {
      case 'success':
        return <IconWrapper><Check size={22} color="#10b981" /></IconWrapper>;
      case 'alert':
        return <IconWrapper><AlertCircle size={22} color="#f59e0b" /></IconWrapper>;
      case 'none':
        return null;
      default:
        return <IconWrapper><AlertCircle size={22} color="#f59e0b" /></IconWrapper>;
    }
  };

  const handleButtonClick = () => {
    setModalOpen(false);
    if (click) click();
  };

  return (
    <StyledModal
      title={
        <>
          {renderIcon()} {msg}
        </>
      }
      centered
      open={modalOpen}
      onCancel={() => !cancel && setModalOpen(false)}
      closeIcon={<X size={18} />}
      footer={
        !footer
          ? [
              <StyledButton
                key="confirm"
                onClick={handleButtonClick}
              >
                {btnName || "OK"}
              </StyledButton>,
            ]
          : false
      }
    >
      <AnimatePresence>
        {modalOpen && (
          <ModalContent
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={contentVariants}
          >
            {children}
          </ModalContent>
        )}
      </AnimatePresence>
    </StyledModal>
  );
};

export default NoResultModal;